import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useLocation } from '@reach/router';
import siteThumbnail from './agency_x.png';

/* prettier-ignore */
const websiteStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Agency X Ireland",
  "alternateName": "Agency X Ireland",
  "url": "https://www.agencyx.ie"
};

function WebsiteStructuredData() {
  return <script type="application/ld+json">{JSON.stringify(websiteStructuredData)}</script>;
}

/* prettier-ignore */
const localBusinessStructuredData = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Agency X",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Charlemont Exchange, Bankside, 13 Charlemont Street, Dublin 2, D02 VN88",
    "addressRegion": "Dublin",
    "postalCode": "Dublin 2",
    "addressCountry": "Ireland"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 53.331149919953745,
    "longitude": -6.260613951016493
  },
  "url": "https://www.agencyx.ie",
  "telephone": "+35316706122",
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Monday",
      "opens": "09:00",
      "closes": "17:30"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Tuesday",
      "opens": "09:00",
      "closes": "17:30"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Wednesday",
      "opens": "09:00",
      "closes": "17:30"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Thursday",
      "opens": "09:00",
      "closes": "17:30"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Friday",
      "opens": "09:00",
      "closes": "17:30"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "00:00",
      "closes": "00:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Sunday",
      "opens": "00:00",
      "closes": "00:00"
    }
  ]
};

function BusinessStructuredData() {
  return <script type="application/ld+json">{JSON.stringify(localBusinessStructuredData)}</script>;
}

// Query for site metadata (used in both components)
const siteMetadataQuery = graphql`
  query {
    site {
      siteMetadata {
        siteTitle
        siteDescription
        siteThumbnailAlt
        siteUrl
        twitterPage
      }
    }
  }
`;

const SEO = ({
  lang = 'en',
  meta = [],
  pageSpecificTitle,
  pageSpecificDescription,
  pageSpecificThumbnail,
  pageSpecificThumbnailAlt,
  children
}) => {
  const data = useStaticQuery(siteMetadataQuery);
  const { siteTitle, siteDescription, siteThumbnailAlt, siteUrl, twitterPage } =
    data.site.siteMetadata;
  const title = pageSpecificTitle || siteTitle;
  const description = pageSpecificDescription || siteDescription;
  const thumbnail = pageSpecificThumbnail || siteThumbnail;
  const thumbnailAlt = pageSpecificThumbnailAlt || siteThumbnailAlt;
  const { pathname } = useLocation();
  const url = `${siteUrl}${pathname}`;
  // pageSpecificThumbnail may be a root-relative path (e.g. "/thumbnails/x.jpg")
  // or an already-absolute CDN URL (e.g. a Sanity image src); only prefix the
  // site URL for the former, otherwise we'd produce "https://…/https://cdn…".
  const image = /^https?:\/\//.test(thumbnail) ? thumbnail : `${siteUrl}${thumbnail}`;

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Agency X',
    url: siteUrl,
    logo: `${siteUrl}${siteThumbnail}`,
    sameAs: [
      'https://www.linkedin.com/company/agencyx-dublin/',
      'https://www.instagram.com/agency_x_dublin'
    ]
  };

  // Combine all meta tags
  const metaTags = [
    { name: "description", content: description },
    { property: "og:site_name", content: siteTitle },
    { property: "og:locale", content: "en_GB" },
    { property: "og:url", content: url },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:image", content: image },
    { property: "og:image:url", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:alt", content: thumbnailAlt },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: image },
    { property: "twitter:image:alt", content: thumbnailAlt },
    { name: "twitter:site", content: twitterPage },
    { name: "twitter:creator", content: twitterPage },
    { name: "twitter:title", content: pageSpecificTitle ? `${siteTitle} | ${pageSpecificTitle}` : siteTitle },
    { name: "twitter:description", content: description }
  ].concat(meta);

  return (
    <>
      <html lang={lang} />
      <title>{title}</title>
      <link rel="canonical" href={url} />
      {metaTags.map((tag, index) => (
        <meta key={`meta-${index}`} {...tag} />
      ))}
      <WebsiteStructuredData />
      <BusinessStructuredData />
      <script type="application/ld+json">{JSON.stringify(organizationStructuredData)}</script>
      {children}
    </>
  );
};

export default SEO;
